import React from 'react';
import styled from '@emotion/styled';
import { useContent } from 'context/ContentContext';
import { useSiteInfo } from 'hooks/useSiteInfo';
import { storageSet } from 'utils/safeStorage';

// Variation 1: Rounded Pills with Sliding Background
const SwitcherContainer = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #f3f4f6;
  border-radius: 9999px;
  padding: 0.375rem;
  gap: 0.25rem;
  position: relative;
`;

const SlidingBackground = styled.div`
  position: absolute;
  top: 0.375rem;
  left: ${({ $isEnglish }) =>
    $isEnglish ? '0.375rem' : 'calc(50% - 0.125rem)'};
  width: calc(50% - 0.125rem);
  height: calc(100% - 0.75rem);
  background-color: white;
  border-radius: 9999px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
`;

const SwitcherButton = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  border: none;
  font-family: inherit;
  background-color: transparent;
  position: relative;
  z-index: 1;
  flex: 1;
  justify-content: center;

  color: ${({ $isActive }) => ($isActive ? '#111827' : '#6b7280')};

  &:hover {
    color: #111827;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    font-size: 1rem;
  }
`;

const LanguageSwitcher = () => {
  const { language, setLanguage } = useContent();
  const { languageSwitcherEnabled } = useSiteInfo();

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    // Persist to localStorage; if the browser blocks it, the choice lasts for
    // this visit only.
    storageSet('localStorage', 'preferredLanguage', newLanguage);
  };

  // Hide component if language switcher is disabled
  if (!languageSwitcherEnabled) {
    return null;
  }

  return (
    <SwitcherContainer role="group" aria-label="Language selector">
      <SlidingBackground $isEnglish={language === 'en'} />
      <SwitcherButton
        onClick={() => handleLanguageChange('en')}
        $isActive={language === 'en'}
        aria-label="Switch to English"
        aria-pressed={language === 'en'}
      >
        <span>🇺🇸</span> EN
      </SwitcherButton>
      <SwitcherButton
        onClick={() => handleLanguageChange('es')}
        $isActive={language === 'es'}
        aria-label="Cambiar a Español"
        aria-pressed={language === 'es'}
      >
        <span>🇪🇸</span> ES
      </SwitcherButton>
    </SwitcherContainer>
  );
};

export default LanguageSwitcher;
